import { supabase, isPlaceholderSupabase } from './supabase';
import { appendLocalPlayer, loadLocalPlayers, removeLocalPlayer } from './demoData';

/**
 * Coach-driven player CRUD. Same shape as the goal/clip/dev-plan
 * helpers: detect placeholder Supabase, persist locally; otherwise
 * write through to the live `users` + `team_players` tables per the
 * 00008 migration shape Codex moved Players over to.
 */

export interface NewPlayer {
  teamId: string;
  name: string;
  /** Free-form position label (GK, CB, ST, etc). */
  position: string | null;
}

export interface AddPlayerResult {
  ok: true;
  playerId: string;
  source: 'live' | 'local';
}

export interface AddPlayerError {
  ok: false;
  error: string;
}

const POSITION_PATTERN = /^[A-Za-z][A-Za-z /-]{0,15}$/;

export function validateNewPlayer(input: NewPlayer): string | null {
  const name = input.name?.trim() ?? '';
  if (name.length < 2) return 'Player name needs at least 2 characters.';
  if (name.length > 60) return 'Player name is too long.';
  if (input.position) {
    const pos = input.position.trim();
    if (pos.length > 0 && !POSITION_PATTERN.test(pos)) {
      return 'Position should be a short label like GK, CB, or ST.';
    }
  }
  return null;
}

export async function addPlayer(opts: NewPlayer): Promise<AddPlayerResult | AddPlayerError> {
  const invalid = validateNewPlayer(opts);
  if (invalid) return { ok: false, error: invalid };

  const cleanName = opts.name.trim();
  const cleanPosition = opts.position?.trim() || null;

  // Offline path — write into the same localStorage roster the
  // demo seed populates. PlayerDetail / Players / useTeamPlayers
  // all read through this list when supabase is the placeholder env.
  if (isPlaceholderSupabase()) {
    try {
      const row = appendLocalPlayer({
        team_id: opts.teamId,
        name: cleanName,
        position: cleanPosition,
      });
      return { ok: true, playerId: row.id, source: 'local' };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Could not save player.' };
    }
  }

  // Live path — players are users with role=PLAYER, joined to the team
  // through team_players (per migration 00008). Insert both rows.
  try {
    const { data: user, error: userError } = await supabase
      .from('users')
      .insert({ name: cleanName, role: 'PLAYER' })
      .select('id')
      .single();
    if (userError || !user) {
      return { ok: false, error: userError?.message ?? 'Could not create player record.' };
    }
    const userId = (user as { id: string }).id;

    const { error: linkError } = await supabase
      .from('team_players')
      .insert({
        team_id: opts.teamId,
        player_id: userId,
        position: cleanPosition,
        is_active: true,
      });
    if (linkError) {
      return { ok: false, error: `Created player but could not attach to team: ${linkError.message}` };
    }
    return { ok: true, playerId: userId, source: 'live' };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Could not save player.' };
  }
}

export interface EditPlayerInput {
  playerId: string;
  name: string;
  position: string | null;
}

export async function editPlayer(opts: EditPlayerInput): Promise<{ ok: true; source: 'live' | 'local' } | { ok: false; error: string }> {
  const invalid = validateNewPlayer({ teamId: '_', name: opts.name, position: opts.position });
  if (invalid) return { ok: false, error: invalid };
  const cleanName = opts.name.trim();
  const cleanPosition = opts.position?.trim() || null;

  if (isPlaceholderSupabase()) {
    try {
      const PLAYERS_KEY = '4cm_local_players';
      const existing = loadLocalPlayers();
      const idx = existing.findIndex((p) => p.id === opts.playerId);
      if (idx === -1) return { ok: false, error: 'Player not found on this device.' };
      const next = existing.slice();
      next[idx] = { ...next[idx], name: cleanName, position: cleanPosition };
      localStorage.setItem(PLAYERS_KEY, JSON.stringify(next));
      return { ok: true, source: 'local' };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Could not update player.' };
    }
  }

  // Live path — name on `users`, position on `team_players`.
  try {
    const { error: nameError } = await supabase
      .from('users')
      .update({ name: cleanName })
      .eq('id', opts.playerId);
    if (nameError) return { ok: false, error: nameError.message };
    const { error: posError } = await supabase
      .from('team_players')
      .update({ position: cleanPosition })
      .eq('player_id', opts.playerId)
      .eq('is_active', true);
    if (posError) {
      return { ok: false, error: `Updated name but could not save position: ${posError.message}` };
    }
    return { ok: true, source: 'live' };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Could not update player.' };
  }
}

export async function removePlayer(playerId: string): Promise<{ ok: true; source: 'live' | 'local' } | { ok: false; error: string }> {
  if (isPlaceholderSupabase()) {
    try {
      removeLocalPlayer(playerId);
      return { ok: true, source: 'local' };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Could not remove player.' };
    }
  }
  // Live path — soft-remove via team_players.is_active so ratings/goals
  // attached to the user survive for historical context.
  try {
    const { error } = await supabase
      .from('team_players')
      .update({ is_active: false })
      .eq('player_id', playerId);
    if (error) return { ok: false, error: error.message };
    return { ok: true, source: 'live' };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Could not remove player.' };
  }
}
